import { useState, useRef, useEffect } from 'react'
import { useBrieflyStorage } from '../hooks/useBrieflyStorage'
import { useDataFromEndpoint } from '../hooks/useDataFromEndpoint'
import { getUserAccessToken, getTranscriptionAsText, generatePromptOutput, copyToClipboard, trackEvent, highlightText, getContextStrings,
  checkIfStandardSummaryIsEligible,
  checkIfCustomSummaryIsEligible 
} from '../../utils/genericUtils'
import { ThreeDots } from 'react-loader-spinner'
import { LoadingSvg } from '../../images/LoadingSvg'
import { PromptTemplateDropdown } from '../controls/PromptTemplateDropdown'
import { Checkbox } from '../controls/Checkbox'
import { IconWithTimeoutReplaceOnClick } from '../../utils/IconWithTimeoutReplaceOnClick'
import { useUserData } from '../hooks/useUserData';

function generateEmptyPrompt() {
  return [{
    label: 'Empty prompt',
    prompt: '',
    isActive: true
  }]
}

export default function MeetingTabOutput({ meetingData, version, isGpt4, searchWords, setShowOutOfCreditsPopup }) {

  const [ { subscription_is_active, admin_user_subscription_is_active, subscription_plan }, forceRenewUserData ] = useUserData()

  const subscriptionIsActive = subscription_is_active || admin_user_subscription_is_active || subscription_plan === 'STANDARD_GIVEN_FOR_FREE'

  const [promptsList, setPromptsList, promptsListIsLoading] = useBrieflyStorage({
    storageKey: meetingData.meetingId + '-customised-prompts',
    defaultValue: generateEmptyPrompt(),
    processOnInitialLoad: list => list.length > 0 ? list.map(x => ({...x, isLoading: false})) : generateEmptyPrompt()
  })

  const [myPrompts, setMyPrompts, myPromptsIsLoading] = useBrieflyStorage({
    storageKey: 'briefly-my-prompts',
    defaultValue: [],
    delay: true,
    processOnInitialLoad: list => list.map(x => typeof x === 'string' ? { prompt: x, label: x} : x)
  })

  const [brieflyRecommendedPrompts, brieflyRecommendedPromptsIsLoading, brieflyRecommendedPromptsError] = useDataFromEndpoint(
    '/api/briefly/briefly_recommended_prompts'
  )

  const [generatedResultBuffer, setGeneratedResultBuffer] = useState(null)

  const [dropdownIsOpen, setDropdownIsOpen] = useState(false)

  const [isMyPromptNamingMode, setIsMyPromptNamingMode] = useState(false)
  const [promptNameValue, setPromptNameValue] = useState("")


  useEffect(() => {
    if(generatedResultBuffer) {
      const { result, originalPrompt, error } = generatedResultBuffer

      console.log('log', result, error, originalPrompt, promptsList)

      if(error){
        setPromptsList(prev => prev.map(x => x.prompt == originalPrompt.prompt ? { ...x, output: null, isLoading: false, error: true } : x))
      } else {
        setPromptsList(prev => prev.map(x => x.prompt == originalPrompt.prompt ? { ...x, output: result, isLoading: false, error: false } : x))
      }

    }
  }, [generatedResultBuffer])

  const currentPrompt = promptsList?.find(x => x.isActive) || promptsList?.[0]

  const newOutputBtnIsActive = !currentPrompt?.output

  useEffect(() => {
    if(searchWords) {
        setTimeout(() => {
          if(!promptsListIsLoading) {
            var promptWithSearchResult = promptsList.find(x => getContextStrings(x.output, searchWords, 1).length > 0)
            setCurrentPrompt(promptWithSearchResult)
          }
        }, 100)
    }
  }, [searchWords])

  function setCurrentPrompt(prompt) {
    setPromptsList(
      promptsList.map(x => x == prompt ? {
        ...x,
        isActive: true
      } : {
        ...x,
        isActive: false
      })
    )
  }

  const promptTextarea = useRef(null)

  function updateCurrentPrompt(newValue) {
      const newPrompt = {
        prompt: newValue,
        label: !newValue ? 'Empty prompt' : newValue.slice(0, 25) + (newValue.length > 25 ? '...' : ''),
        isActive: true
      }

      setPromptsList(promptsList.map(item => item == currentPrompt ? newPrompt : {...item, isActive: false}))
  }

  function createNewPrompt() {
    trackEvent("briefly-create-create-new-prompt", {
      "total-prompts-open-before-create": promptsList.length
    })

    if(promptsList.find(x => x.prompt === '')) {
      setCurrentPrompt(promptsList.find(x => x.prompt === ''))
      return;
    }
    const newPrompt = {
      label: 'Empty prompt',
      prompt: '',
      isActive: true
    }
    setPromptsList(promptsList.map(x => ({...x, isActive: false})).concat([newPrompt]))
  }

  function deleteCurrentPrompt() {
    console.log('deleting', promptsList.filter(x => x != currentPrompt))
    setPromptsList(promptsList.filter(x => x != currentPrompt))
  }

  function generateOutput() {

    checkIfCustomSummaryIsEligible(getTranscriptionAsText(meetingData), currentPrompt.prompt, isEligible => {
      if(isEligible) {
        trackEvent("briefly-create-generate-output", {
          "prompt": currentPrompt.prompt
        })

        const promptBeingProcessedLoadingState = {
          ...currentPrompt,
          isLoading: true,
          error: false
        }

      setPromptsList(promptsList.map(item => item == currentPrompt ? promptBeingProcessedLoadingState : item))

        getUserAccessToken(({ access }) => {
          generatePromptOutput(meetingData, promptBeingProcessedLoadingState, access, version, isGpt4).then(response => {
              forceRenewUserData()
              setGeneratedResultBuffer({
                originalPrompt: promptBeingProcessedLoadingState,
                result: response.result,
                error: response.error
              })
          })
        })
      } else {
        setShowOutOfCreditsPopup(true)
      }
    })

    
  }

  if(promptsListIsLoading) {
    return ''
  }

  const generateButtonIsActive = currentPrompt?.prompt && !currentPrompt?.output

  const brieflyRecommendedPromptsInBottom = brieflyRecommendedPrompts?.prompts?.filter(p => p.show_in_bottom_panel)

  return (
    <div id="meeting-tab-output-container">
      <div id="output-header">
        <h4>Turn your meeting transcripts into deliverables</h4>

        <button className="inline" onClick={createNewPrompt} style={{ height: 40, width: 172,padding: 10 }} disabled={newOutputBtnIsActive}>
          <svg style={{ position: 'relative', left: -5}} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 7C14 7.54487 13.5583 7.98658 13.0134 7.98658H7.98658V13.0134C7.98658 13.5583 7.54487 14 7 14V14C6.45513 14 6.01342 13.5583 6.01342 13.0134V7.98658H0.986577C0.441705 7.98658 0 7.54487 0 7V7C0 6.45513 0.441706 6.01342 0.986578 6.01342H6.01342V0.986577C6.01342 0.441705 6.45513 0 7 0V0C7.54487 0 7.98658 0.441706 7.98658 0.986578V6.01342H13.0134C13.5583 6.01342 14 6.45513 14 7V7Z" fill="white"/>
          </svg>

          New output
        </button>
      </div>
      <div id="meeting-tab-output">
        <div>
          <div>
            <PromptTemplateDropdown myPrompts={myPrompts} currentPrompt={currentPrompt} onPromptSave={() => {
                setMyPrompts(myPrompts.concat([ currentPrompt?.prompt ]))
              }} 
              onSelectPrompt={prompt => {
                trackEvent("briefly-create-use-saved-prompt", {
                  "prompt": prompt.prompt,
                  "label": prompt.label,
                  "source": "DROPDOWN"
                })

                updateCurrentPrompt(prompt.prompt)

                setIsMyPromptNamingMode(false)
              }}
              brieflyRecommendedPrompts={brieflyRecommendedPrompts?.prompts}
              isOpen={dropdownIsOpen}
              setIsOpen={setDropdownIsOpen}
              onDeleteMyPrompt={prompt => {
                setMyPrompts(myPrompts.filter(p => p !== prompt))
              }}
              onEditMyPromptName={(prompt, newName) => {
                setMyPrompts(myPrompts.map(p => p === prompt ? { label: newName, prompt: p.prompt } : p ))
              }}
            />
            <div className={"textarea-container " + (brieflyRecommendedPrompts?.prompts?.length > 0 && 'recommended-prompts-after')}>
              <textarea maxLength="1200" disabled={currentPrompt.isLoading} value={currentPrompt.prompt} onChange={e => updateCurrentPrompt(e.target.value)} ref={promptTextarea} />

              {currentPrompt.prompt === '' ? <div className="textarea-placeholder" onClick={() => promptTextarea.current.focus()}>
                  <span>Enter your custom prompt...</span>
                  <br/><br/>
                  <i>Try something like Make this brainstorm session into a product requirement doc or Turn the transcript into a sentiment analysis, make it short and user a friendly tone.</i>
              </div> : null}

              <div className="checkbox-container" 
                  style={!currentPrompt.prompt || brieflyRecommendedPrompts?.prompts?.find(x => x.prompt === currentPrompt.prompt) ? {pointerEvents: 'none', opacity: 0.7} : {} }>
                {!isMyPromptNamingMode ? <Checkbox isChecked={myPrompts?.find(x => x.prompt === currentPrompt.prompt) || brieflyRecommendedPrompts?.prompts?.find(p => p.prompt == currentPrompt.prompt)} label="Save this prompt template" setIsChecked={isChecked => {
                  if(isChecked) {

                    setIsMyPromptNamingMode(true)
                    setPromptNameValue("")

                    // trackEvent("briefly-create-save-prompt", {
                    //   "prompt": currentPrompt.prompt
                    // })

                    // setMyPrompts(myPrompts.concat([currentPrompt.prompt]))
                  } else {
                    setMyPrompts(myPrompts.filter(p => p.prompt !== currentPrompt.prompt))
                  }
                }}/> : ''}

                {isMyPromptNamingMode ? <div className="my-prompt-naming-new">
                  <div className="svg" onClick={() => setIsMyPromptNamingMode(false)}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11.396 0.604027C11.7296 0.937622 11.7296 1.47849 11.396 1.81208L7.20805 6L11.396 10.1879C11.7296 10.5215 11.7296 11.0624 11.396 11.396C11.0624 11.7296 10.5215 11.7296 10.1879 11.396L6 7.20805L1.81208 11.396C1.47849 11.7296 0.937622 11.7296 0.604027 11.396C0.270432 11.0624 0.270432 10.5215 0.604027 10.1879L4.79195 6L0.604027 1.81208C0.270432 1.47849 0.270432 0.937622 0.604027 0.604027C0.937622 0.270432 1.47849 0.270432 1.81208 0.604027L6 4.79195L10.1879 0.604027C10.5215 0.270432 11.0624 0.270432 11.396 0.604027Z" fill="#C6C6C6"/>
                    </svg>
                  </div>

                  <input type="text" placeholder="Name Your Prompt" value={promptNameValue} onChange={e => setPromptNameValue(e.target.value)}/>

                  <button disabled={!promptNameValue} onClick={() => {
                    trackEvent("briefly-create-save-prompt", {
                      "prompt": currentPrompt.prompt,
                      "label": promptNameValue
                    })

                    setMyPrompts(myPrompts.concat([{prompt: currentPrompt.prompt, label: promptNameValue}]))

                    setIsMyPromptNamingMode(false)
                  }}>
                    Save
                  </button>

                </div> : ''}
              </div>
            </div>
            <button disabled={!generateButtonIsActive} onClick={generateOutput}>
              {currentPrompt.isLoading ? <div style={{ display: 'flex', justifyContent: 'center' }}>
                <ThreeDots 
                      height="20" 
                      width="20" 
                      radius="9"
                      color="white" 
                      ariaLabel="three-dots-loading"
                      wrapperStyle={{}}
                      wrapperClassName=""
                      visible={true}
                 />
              </div> : <>
                          Generate output {!subscriptionIsActive ? '[1 AI Credit]' : ''}
                          <svg width="9" height="15" viewBox="0 0 9 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0.659696 1.17207C1.00287 0.804049 1.5822 0.79211 1.94024 1.14568L8.375 7.5L1.94025 13.8543C1.5822 14.2079 1.00287 14.196 0.659696 13.8279V13.8279C0.327999 13.4722 0.341968 12.9165 0.691116 12.5779L5.92722 7.5L0.691116 2.42212C0.341967 2.08352 0.327999 1.52778 0.659696 1.17207V1.17207Z" fill="white"/>
                          </svg>
                       </>}
              

            </button>
          </div>

          {brieflyRecommendedPromptsInBottom?.length > 0 && <div className="recommended-prompts">
            <h2>Try these prompts for your call</h2>

            <div className="recommended-prompts-container">
              {brieflyRecommendedPromptsInBottom?.map(({ prompt, label }) => <div onClick={() => {
                trackEvent("briefly-create-use-saved-prompt", {
                  "prompt": prompt,
                  "label": label,
                  "source": "BOTTOM_PANEL"
                })
                updateCurrentPrompt(prompt)
                setIsMyPromptNamingMode(false)
              }}>
                  {label}
              </div>)}
            </div>

            <h5 onClick={() => setDropdownIsOpen(true)}>View all prompts</h5>
          </div>}
        </div>
        <div id="ai-output-list" className={'_' + Math.min(promptsList.length, 7) + '-prompts'}>
          {promptsList.map((item, i) => {
            const { label, prompt, isLoading, output, error } = item
            const isActive = item === currentPrompt
            if(isActive) {
              return <div id="ai-output" className={"gradient-border-container " + (currentPrompt.error && 'error ') + (output && ' loaded')}>
                <div className="gradient-border"/>
                <div className="gradient-content">
                  <h5>

                    <div>
                      <span className="copy-btn-container" style={output ? {} : { width: 0 }}>
                        {output ? <IconWithTimeoutReplaceOnClick onClick={() => {

                          trackEvent("briefly-create-copy-ai-output", {
                            "prompt": currentPrompt.prompt,
                            "output": output
                          })

                          copyToClipboard(output)

                        }} initialIcon={<svg  style={{cursor: 'pointer'}} width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12.3234 14.5362V4.35745H4.35745V14.5362H12.3234ZM12.3234 2.92766C12.7092 2.92766 13.0496 3.07518 13.3447 3.37021C13.6397 3.64255 13.7872 3.97163 13.7872 4.35745V14.5362C13.7872 14.922 13.6397 15.2624 13.3447 15.5574C13.0496 15.8525 12.7092 16 12.3234 16H4.35745C3.97163 16 3.63121 15.8525 3.33617 15.5574C3.04113 15.2624 2.89362 14.922 2.89362 14.5362V4.35745C2.89362 3.97163 3.04113 3.64255 3.33617 3.37021C3.63121 3.07518 3.97163 2.92766 4.35745 2.92766H12.3234ZM10.1447 0V1.46383H1.42979V11.6426H0V1.46383C0 1.07801 0.13617 0.737589 0.408511 0.442553C0.703546 0.147518 1.04397 0 1.42979 0H10.1447Z" fill="#E44867"/>
                        </svg>} clickedIcon={<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#E44867" d="M6.38498 12.0188L13.5962 4.80751L12.4695 3.64319L6.38498 9.7277L3.53052 6.87324L2.40376 8L6.38498 12.0188ZM2.32864 2.3662C3.9061 0.788732 5.79656 0 8 0C10.2034 0 12.0814 0.788732 13.6338 2.3662C15.2113 3.91862 16 5.79656 16 8C16 10.2034 15.2113 12.0939 13.6338 13.6714C12.0814 15.2238 10.2034 16 8 16C5.79656 16 3.9061 15.2238 2.32864 13.6714C0.776213 12.0939 0 10.2034 0 8C0 5.79656 0.776213 3.91862 2.32864 2.3662Z"/>
                          </svg>
                        }/>  : <span></span>}
                      </span>

                      <span>AI Output</span>
                    </div>

                    {promptsList.length > 1 || currentPrompt?.prompt ? <svg onClick={() => {
                      if(promptsList.length === 1) {
                        updateCurrentPrompt("")
                      } else {
                        setPromptsList(promptsList.filter(x => x !== currentPrompt))
                      }
                    }} style={{ cursor: 'pointer'}} width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.75 12.9449C1.75 13.6832 2.32422 14.2574 3.0625 14.2574H10.9375C11.6484 14.2574 12.25 13.6832 12.25 12.9449V3.75745H1.75V12.9449ZM9.1875 5.94495C9.1875 5.7262 9.37891 5.50745 9.625 5.50745C9.84375 5.50745 10.0625 5.7262 10.0625 5.94495V12.0699C10.0625 12.316 9.84375 12.5074 9.625 12.5074C9.37891 12.5074 9.1875 12.316 9.1875 12.0699V5.94495ZM6.5625 5.94495C6.5625 5.7262 6.75391 5.50745 7 5.50745C7.21875 5.50745 7.4375 5.7262 7.4375 5.94495V12.0699C7.4375 12.316 7.21875 12.5074 7 12.5074C6.75391 12.5074 6.5625 12.316 6.5625 12.0699V5.94495ZM3.9375 5.94495C3.9375 5.7262 4.12891 5.50745 4.375 5.50745C4.59375 5.50745 4.8125 5.7262 4.8125 5.94495V12.0699C4.8125 12.316 4.59375 12.5074 4.375 12.5074C4.12891 12.5074 3.9375 12.316 3.9375 12.0699V5.94495ZM12.6875 1.13245H9.40625L9.13281 0.640259C9.02344 0.421509 8.77734 0.257446 8.55859 0.257446H5.41406C5.19531 0.257446 4.92187 0.421509 4.83984 0.640259L4.59375 1.13245H1.3125C1.06641 1.13245 0.875 1.3512 0.875 1.56995V2.44495C0.875 2.69104 1.06641 2.88245 1.3125 2.88245H12.6875C12.9062 2.88245 13.125 2.69104 13.125 2.44495V1.56995C13.125 1.3512 12.9062 1.13245 12.6875 1.13245Z" fill="#E44867"/>
                    </svg> : ''}


                  </h5>
                  <p>
                    {isLoading ? <div className="output-loader">
                      <LoadingSvg/>
                      <div className="loader-container">
                        <div className="loader-inner"/>
                      </div>
                      <h3>Please wait while we create your AI output...</h3>
                      <div class="typewriter-container">
                        <div class="typewriter line-1">
                          <div>Analyzing your transcript...</div>
                        </div>
                         <div class="typewriter line-2">
                          <div>Processing the information...</div>
                        </div>
                         <div class="typewriter line-3">
                          <div>Thinking...</div>
                        </div>
                         <div class="typewriter line-4">
                          <div>Generating results...</div>
                        </div>
                         <div class="typewriter line-5">
                          <div>It's taking longer than usual. We're still working on it...</div>
                        </div>
                      </div> 
                    </div>
                    : output ? output.split('\n').map((x, i) => <div key={i}>{searchWords ? highlightText(x, searchWords, {background: '#F9C338', color: 'black'}) : x}</div>) : error ? <>
                      <div className="error">There was an issue with OpenAI while generating your output, try again later...</div>
                    </> : <span style={{opacity: 0.5}}>Create or choose a prompt to generate your personalised AI output...</span>}
                  </p>
                </div>
              </div>
            } else {
              return <div className="ai-output-collapsed" onClick={() => setCurrentPrompt(item)}>
                <div>
                  {isLoading ? <ThreeDots 
                        height="20" 
                        width="20" 
                        radius="9"
                        color="white" 
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible={true}
                         /> : ''}

                  {prompt || "Empty prompt"}
                </div>

                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                  position: 'relative',
                  top: 1
                }}>
                  <path d="M0.576058 0.761882C0.260613 1.05603 0.25038 1.5526 0.553439 1.8595L6 7.375L11.4466 1.8595C11.7496 1.5526 11.7394 1.05603 11.4239 0.761883C11.119 0.477571 10.6427 0.489544 10.3525 0.788814L6 5.2769L1.64753 0.788814C1.35731 0.489544 0.880954 0.477571 0.576058 0.761882Z" fill="white"/>
                </svg>

              </div>
            }
          })}
          

        </div>
      </div>
    </div>
  );
}
